import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { Store } from '@ngrx/store';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { MaterialModule } from '@sharedModule/material/material.module';
import { Country } from '@sharedModule/models/country.model';
import { GlobalService } from '@sharedModule/services/global.service';
import { selectCountry } from '@sharedModule/store/country/country.selector';
import { getCountryInfoFromCountryName } from '@sharedModule/utils/array.utils';
import { Observable, Subject, takeUntil } from 'rxjs';
import { CountrySettingsService } from '../../services/country-settings.service';
import { UpsertSourceCountryInterface } from '../../types/upsert-source-country';
import { loadSourceCountry } from '@sharedModule/store/source-country/source-country.actions';

@Component({
  selector: 'app-upsert-source-country',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule, InputModule],
  templateUrl: './upsert-source-country.component.html',
  styleUrl: './upsert-source-country.component.scss',
})
export class UpsertSourceCountryComponent implements OnInit, OnDestroy {
  public readonly attrConstant =
    ATTRIBUTES_CONSTANT.settings.countrySettings.upsertCountry;
  upsertSourceCountryForm!: FormGroup;
  countryList!: Observable<Country[]>;
  isEdit!: boolean;
  #destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private router: Router,
    private globalService: GlobalService,
    private messageHandlerService: MessageHandlerService,
    private countrySettingsService: CountrySettingsService
  ) {}

  ngOnInit(): void {
    this.upsertSourceCountryForm = new FormGroup({
      [this.attrConstant.srcCountryName.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.srcCountryCode.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.srcCountryAlpha2Code.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.srcCurrencySupported.key]: new FormControl('', [
        Validators.required,
      ]),
      [this.attrConstant.srcMobileCode.key]: new FormControl('', [
        Validators.required,
      ]),
    });
    this.countryList = this.store.select(selectCountry);
    this.isEdit = this._isEdit(this.router.url);
  }

  onCountryChange(event: {
    value: string;
    selectOptions: Country[] | null;
  }): void {
    const selectedCountry = getCountryInfoFromCountryName(
      event.value,
      event.selectOptions
    );
    if (selectedCountry)
      this.upsertSourceCountryForm.patchValue({
        [this.attrConstant.srcCountryCode.key]: selectedCountry.alpha3,
        [this.attrConstant.srcCountryAlpha2Code.key]: selectedCountry.alpha2,
        [this.attrConstant.srcCurrencySupported.key]: selectedCountry.currency,
        [this.attrConstant.srcMobileCode.key]: selectedCountry.telephone_code,
      });
  }

  private _isEdit = (url: string): boolean => {
    const endUrl = url.split('/');
    if (endUrl[2] == 'edit-source-country') {
      this.patchFormValue();
      return true;
    } else {
      return false;
    }
  };

  patchFormValue(): void {
    this.globalService
      .getDataStream()
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: data => {
          if (Object.keys(data).length === 0) this.redirectTo();
          else
            this.upsertSourceCountryForm.patchValue({
              [this.attrConstant.srcCountryName.key]: data.countryName,
              [this.attrConstant.srcCountryCode.key]: data.countryCode,
              [this.attrConstant.srcCountryAlpha2Code.key]:
                data.countryAlpha2Code,
              [this.attrConstant.srcCurrencySupported.key]:
                data.currencySupported[0].currency,
              [this.attrConstant.srcMobileCode.key]: data.mobileCode,
            });
        },
      });
  }

  onSubmit(): void {
    if (this.upsertSourceCountryForm.valid) {
      const formData = this.upsertSourceCountryForm.value;
      const payload = {
        ...formData,
        srcCurrencySupported: [
          { currency: formData[this.attrConstant.srcCurrencySupported.key] },
        ],
      };
      if (this.isEdit) this.updateSourceCountry(payload);
      else this.addSourceCountry(payload);
    }
  }

  addSourceCountry(formValue: UpsertSourceCountryInterface): void {
    this.countrySettingsService
      .addSourceCountry(formValue)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.store.dispatch(loadSourceCountry({}));
            this.messageHandlerService.show(x.message);
            this.redirectTo();
          }
        },
      });
  }

  updateSourceCountry(formValue: UpsertSourceCountryInterface): void {
    this.countrySettingsService
      .updateSourceCountry(formValue)
      .pipe(takeUntil(this.#destroy$))
      .subscribe({
        next: x => {
          if (x.success) {
            this.store.dispatch(loadSourceCountry({}));
            this.messageHandlerService.show(x.message);
            this.redirectTo();
          }
        },
      });
  }

  redirectTo(): void {
    this.globalService.currentTabIndex = 0;
    this.router.navigate(['/settings/country']);
  }

  ngOnDestroy(): void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }
}
